import {
  DialogCloseReason,
  DialogService,
  SuiDialogBase,
  injectDirtyCloseHandler,
} from '@sui/dialog';
import { Injector, computed, inject, signal } from '@angular/core';

import { FormGroup } from '@angular/forms';
import { UnsavedChangesDialogComponent } from 'src/app/shared/components/unsaved-changes-dialog/unsaved-changes-dialog.component';
import { firstValueFrom } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';

// * Interface describing the required contract for form editor components.
interface IFormEditorComponent {
  formGroup: FormGroup;
  formConfig: any; // ! FormConfiguration - using 'any' to avoid circular dependency
  selectedTabKey: string;
  activeTabIndex: number;
  /** Returns the in-flight save when the editor saves through a mutation, nothing on the NgRx form path. */
  submit(): Promise<unknown> | void;
  /** A waiting dialog's slot setter (WAP-1857); the editor presents a refusal through it. */
  dialogInlineError?: (message: string) => void;
  validateAndHandleInvalidForm(): boolean;
}

/**
 * Base class for dialogs using GeneralFormEditorComponent with CDK portals.
 *
 * @template TEditor - Type of editor component (extends GeneralFormEditorComponent)
 * @template TData - Type of data passed TO the dialog (via config.data)
 * @template TResult - Type of data returned FROM the dialog (via close)
 */
export abstract class FormEditorDialogBase<
  TEditor extends IFormEditorComponent,
  TData = unknown,
  TResult = unknown,
> extends SuiDialogBase<TData, TResult> {
  protected abstract editorComponent: TEditor;

  protected dialogService = inject(DialogService);
  protected dirtyHandler = injectDirtyCloseHandler(this.dialogRef);
  private injector = inject(Injector);

  /**
   * The rule since WAP-1976: a dialog the user typed into waits for its save, closes
   * on success and keeps the form on a refusal, whose text the editor puts in the
   * dialog's slot. Only an editor on the NgRx path still closes first (its submit
   * returns no promise). Off is the optimistic close, kept for a subclass that
   * must have it.
   */
  protected readonly awaitSave: boolean = true;

  /** True while an awaited save is in flight; the footer's Save button binds it. */
  readonly saving = signal(false);

  private _isFormValid = computed(() => false);
  private _isFormDirty = computed(() => false);

  get isFormValid(): boolean {
    return this.editorComponent?.formGroup?.valid ?? false;
  }

  get isFormDirty(): boolean {
    return this.editorComponent?.formGroup?.dirty ?? false;
  }

  // * Initialize form editor dirty-close protection.
  // * Call this in ngAfterViewInit() after:
  // * - ViewChild editorComponent is available
  // * - Portals are setup
  protected initializeFormEditorProtection(): void {
    if (this.awaitSave) {
      this.editorComponent.dialogInlineError = this.showApiError;
    }
    const formStatus = toSignal(this.editorComponent.formGroup.statusChanges, {
      initialValue: this.editorComponent.formGroup.status,
      injector: this.injector,
    });

    const formValue = toSignal(this.editorComponent.formGroup.valueChanges, {
      initialValue: this.editorComponent.formGroup.value,
      injector: this.injector,
    });

    this._isFormValid = computed(() => {
      formStatus();
      return this.editorComponent.formGroup.valid;
    });

    this._isFormDirty = computed(() => {
      formValue();
      return this.editorComponent.formGroup.dirty;
    });

    this.dirtyHandler.protectClosingWhenDirty(
      this._isFormDirty,
      async () => {
        if (!this._isFormDirty()) {
          // * аllow close
          return true;
        }

        const dialogRef = this.dialogService.openSimpleDialog(
          UnsavedChangesDialogComponent,
          {
            data: {
              isValid: this._isFormValid,
            },
            closeOnClick: false,
          },
        );

        const confirmResult = await firstValueFrom(dialogRef.closed);

        if (confirmResult.reason === DialogCloseReason.SaveAndClose) {
          if (!this._isFormValid()) {
            this.handleValidationErrors();
            // ! don't close, validation failed
            return false;
          }

          this.clearApiError();
          const saved = this.performSave() ?? undefined;
          this.closeSaved(this.editorComponent.formGroup.value, saved);

          // ! closed by closeSaved, at once or once the save settled
          return false;
        }

        if (confirmResult.reason === DialogCloseReason.DiscardChanges) {
          // * allow close without saving
          return true;
        }

        // * user chose "Cancel" / ESC / backdrop click
        return false; // ! don't close
      },
      this.saving,
    );
  }

  private handleValidationErrors(): void {
    this.editorComponent.validateAndHandleInvalidForm();
  }

  /**
   * Runs the editor's save and hands back its promise (mutation path) so the close
   * event can carry it as `saved` - the dialog closes optimistically, and the opener
   * refreshes its row only once that promise resolved (`editorSaved`).
   */
  protected performSave(): Promise<unknown> | void {
    return this.editorComponent.submit();
  }

  public save(): void {
    const formGroup = this.editorComponent?.formGroup;
    if (!formGroup) {
      console.error('Form group not available');
      return;
    }

    if (formGroup.invalid) {
      this.handleValidationErrors();
      return;
    }

    this.clearApiError();
    const saved = this.performSave() ?? undefined;
    this.closeSaved(formGroup.value, saved);
  }

  /**
   * Closes with `{ formData, saved }` (see SavedCloseData in editor-close.utils.ts): at
   * once by default, or once the save settled when `awaitSave` is on - a refusal keeps
   * the form, its text already in the slot through the editor's `dialogInlineError`.
   */
  private closeSaved(formData: unknown, saved: Promise<unknown> | void): void {
    const options = {
      reason: DialogCloseReason.Save,
      data: { formData, saved } as TResult,
    };
    if (!this.awaitSave || !saved) {
      this.dirtyHandler.closeImmediately(options);
      return;
    }
    this.saving.set(true);
    saved
      .then(() => this.dirtyHandler.closeImmediately(options))
      .catch(() => undefined)
      .finally(() => this.saving.set(false));
  }

  public async cancel(): Promise<void> {
    await this.dirtyHandler.tryClose({
      reason: DialogCloseReason.Cancel,
    });
  }
}

// * Example usage:
/*
export class EditorRoleDialogComponent extends FormEditorDialogBase<
  EditorRoleComponent,
  DialogData,
  DialogOutput
> {
  @ViewChild(EditorRoleComponent)
  protected override editorComponent!: EditorRoleComponent;

  ngAfterViewInit() {
    // Setup portals
    this.tabsPortal = this.portalRegistry.get('general-form-editor-tabs');
    this.formPortal = this.portalRegistry.get('general-form-editor-form');

    // Wire up form editor protection (tab jumping enabled by default)
    this.initializeFormEditorProtection();

    // Or disable tab jumping if not needed:
    // this.initializeFormEditorProtection({ enableTabJumping: false });
  }
}
*/
